/**
 * Android shell updater.
 * The manifest is public on the companion Pages site. The app never calls GitHub.
 * Web content is the copy bundled in the APK. This file is only the shell update.
 */

export const APK_MANIFEST_URL = "https://northstar-companion.pages.dev/apk/apk-latest.json";
export const APK_PUBLIC_HOST = "northstar-companion.pages.dev";
export const APK_CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000;
export const APK_CHECK_STORAGE_KEY = "ns.companion.apkCheckAt";

export type ApkManifest = {
  versionCode: number;
  versionName: string;
  notes: string;
  url: string;
  sha256: string;
};

export type UpdatePhase = "hidden" | "available" | "downloading" | "ready" | "error";

export type UpdateDecision = "available" | "current" | "browser" | "invalid";

export type UpdateBarState = {
  phase: UpdatePhase;
  versionName: string;
  versionCode: number;
  notes: string;
  url: string;
  sha256: string;
  progress: number;
  received: number;
  total: number | null;
  error: string;
};

export const HIDDEN_UPDATE: UpdateBarState = {
  phase: "hidden",
  versionName: "",
  versionCode: 0,
  notes: "",
  url: "",
  sha256: "",
  progress: 0,
  received: 0,
  total: null,
  error: "",
};

export function apkUrlAllowed(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname === APK_PUBLIC_HOST && parsed.pathname.endsWith(".apk");
  } catch {
    return false;
  }
}

export function isSha256Hex(value: string): boolean {
  return /^[a-f0-9]{64}$/i.test(value.trim());
}

export function sha256Matches(actual: string, expected: string): boolean {
  return isSha256Hex(actual) && isSha256Hex(expected) && actual.trim().toLowerCase() === expected.trim().toLowerCase();
}

export function parseApkManifest(raw: unknown): ApkManifest | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const versionName = typeof row.versionName === "string" ? row.versionName.trim() : "";
  const versionCode = typeof row.versionCode === "number" ? row.versionCode : Number(row.versionCode);
  const notes = typeof row.notes === "string" ? row.notes.trim() : "";
  const url = typeof row.url === "string" ? row.url.trim() : "";
  const sha256 = typeof row.sha256 === "string" ? row.sha256.trim().toLowerCase() : "";
  if (!/^\d+\.\d+\.\d+$/.test(versionName)) return null;
  if (!Number.isInteger(versionCode) || versionCode < 1) return null;
  if (!apkUrlAllowed(url)) return null;
  if (!isSha256Hex(sha256)) return null;
  return { versionCode, versionName, notes, url, sha256 };
}

export function parseVersionCode(build: string | number | null | undefined): number | null {
  const code = typeof build === "number" ? build : Number(String(build ?? "").trim());
  if (!Number.isInteger(code) || code < 1) return null;
  return code;
}

/** Newer versionCode only. Equal or older stays quiet. A browser has no shell version. */
export function decideUpdateOffer(localCode: number | null, manifest: ApkManifest | null): UpdateDecision {
  if (!manifest) return "invalid";
  if (localCode == null) return "browser";
  if (manifest.versionCode > localCode) return "available";
  return "current";
}

export function apkUpdateAvailable(localVersionCode: number, remoteVersionCode: number): boolean {
  return decideUpdateOffer(localVersionCode, {
    versionCode: remoteVersionCode,
    versionName: "0.0.0",
    notes: "",
    url: `https://${APK_PUBLIC_HOST}/apk/northstar-companion.apk`,
    sha256: "ab".repeat(32),
  }) === "available" && remoteVersionCode > localVersionCode;
}

export function shouldCheckForApkUpdate(input: {
  now: number;
  lastCheckAt: number | null;
  online: boolean;
  manual: boolean;
  intervalMs?: number;
}): boolean {
  if (!input.online) return false;
  if (input.manual) return true;
  if (input.lastCheckAt == null || !Number.isFinite(input.lastCheckAt)) return true;
  const interval = input.intervalMs ?? APK_CHECK_INTERVAL_MS;
  return input.now - input.lastCheckAt >= interval;
}

export function readLastApkCheck(storage?: Pick<Storage, "getItem"> | null): number | null {
  const raw = storage?.getItem(APK_CHECK_STORAGE_KEY);
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function writeLastApkCheck(now: number, storage?: Pick<Storage, "setItem"> | null): void {
  storage?.setItem(APK_CHECK_STORAGE_KEY, String(now));
}

export type UpdateEvent =
  | { type: "offer"; manifest: ApkManifest }
  | { type: "download" }
  | { type: "progress"; received: number; total: number | null }
  | { type: "verified" }
  | { type: "fail"; error: string }
  | { type: "install-error"; error: string }
  | { type: "clear" };

function withManifest(manifest: ApkManifest, extra: Partial<UpdateBarState> = {}): UpdateBarState {
  return {
    ...HIDDEN_UPDATE,
    versionName: manifest.versionName,
    versionCode: manifest.versionCode,
    notes: manifest.notes,
    url: manifest.url,
    sha256: manifest.sha256,
    ...extra,
  };
}

export function reduceUpdate(state: UpdateBarState, event: UpdateEvent): UpdateBarState {
  if (event.type === "clear") return { ...HIDDEN_UPDATE };
  if (event.type === "offer") {
    if (state.phase === "downloading" || state.phase === "ready") return state;
    return withManifest(event.manifest, { phase: "available" });
  }
  if (event.type === "download") {
    if (state.phase !== "available" && state.phase !== "error") return state;
    return { ...state, phase: "downloading", progress: 0, received: 0, total: null, error: "" };
  }
  if (event.type === "progress") {
    if (state.phase !== "downloading") return state;
    const total = event.total != null && event.total > 0 ? event.total : null;
    const progress = total ? Math.max(0, Math.min(1, event.received / total)) : state.progress;
    return { ...state, received: event.received, total, progress };
  }
  if (event.type === "verified") {
    if (state.phase !== "downloading") return state;
    return { ...state, phase: "ready", progress: 1, error: "" };
  }
  if (event.type === "fail") {
    if (state.phase === "hidden") return state;
    return { ...state, phase: "error", error: event.error };
  }
  if (event.type === "install-error") {
    if (state.phase !== "ready" && state.phase !== "error") return state;
    return { ...state, phase: "ready", error: event.error };
  }
  return state;
}

export function updateBarLabel(state: UpdateBarState): string {
  const version = state.versionName ? ` · v${state.versionName}` : "";
  if (state.phase === "downloading") {
    const pct = state.total ? ` ${Math.round(state.progress * 100)}%` : "";
    return `Downloading${version}${pct}`;
  }
  if (state.phase === "ready") return `Tap to install${version}`;
  if (state.phase === "error") return state.error || "Update failed";
  return `Update Northstar Dispatch${version}`;
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export type ManifestCheck = {
  checked: boolean;
  decision: UpdateDecision;
  manifest: ApkManifest | null;
  message: string;
};

export async function checkApkManifest(input: {
  manual: boolean;
  online: boolean;
  now: number;
  lastCheckAt: number | null;
  localCode: number | null;
  fetchManifest: () => Promise<unknown>;
  intervalMs?: number;
}): Promise<ManifestCheck> {
  if (!input.online) {
    return {
      checked: false,
      decision: "invalid",
      manifest: null,
      message: input.manual ? "Offline. The update check runs again when this phone is online." : "",
    };
  }
  if (!shouldCheckForApkUpdate(input)) {
    return { checked: false, decision: "current", manifest: null, message: "" };
  }
  try {
    const manifest = parseApkManifest(await input.fetchManifest());
    const decision = decideUpdateOffer(input.localCode, manifest);
    if (decision === "invalid") {
      return {
        checked: true,
        decision,
        manifest: null,
        message: input.manual ? "The update manifest could not be read." : "",
      };
    }
    if (decision === "browser") {
      return {
        checked: true,
        decision,
        manifest,
        message: input.manual
          ? `Published shell is v${manifest?.versionName ?? ""}. Install it from the Android app.`
          : "",
      };
    }
    if (decision === "current") {
      return {
        checked: true,
        decision,
        manifest,
        message: input.manual ? "This shell is current." : "",
      };
    }
    return {
      checked: true,
      decision,
      manifest,
      message: manifest?.notes ?? "",
    };
  } catch {
    return {
      checked: false,
      decision: "invalid",
      manifest: null,
      message: input.manual ? "The update check failed. The flight screens stay as they are." : "",
    };
  }
}
